import React from "react";
import { Box, Typography, Container } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import useSEO from "../../../seo/use-seo";
import StandardPageHelmet from "../../../seo/components/StandardPageHelmet";

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(4),
    backgroundColor: "#0f0f0f",
    minHeight: "100vh",
    color: "#fff",
    lineHeight: 1.6,
  },
  title: {
    color: "#FE5A97",
    marginBottom: theme.spacing(3),
    fontWeight: "bold",
  },
  subtitle: {
    color: "#FE5A97",
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    fontWeight: "bold",
  },
  paragraph: {
    marginBottom: theme.spacing(2),
    color: "#fff",
  },
  listItem: {
    marginBottom: theme.spacing(1),
    color: "#fff",
    paddingLeft: theme.spacing(2),
  },
  contactInfo: {
    backgroundColor: "#1a1a1a",
    border: "1px solid #333",
    borderRadius: "8px",
    padding: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  lastUpdated: {
    color: "#ccc",
    fontStyle: "italic",
    marginBottom: theme.spacing(2),
  },
  link: {
    color: "#FE5A97",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
}));

export default function AcceptableUsePolicy() {
  const classes = useStyles();
  const seo = useSEO("acceptable-use-policy");

  return (
    <>
      <StandardPageHelmet seo={seo} />
      <Container maxWidth="md" className={classes.container}>
        <Typography variant="h3" className={classes.title}>
          Acceptable Use Policy
        </Typography>

        <Typography variant="body2" className={classes.lastUpdated}>
          Effective Date: June 30, 2025
        </Typography>

        <Typography variant="body1" className={classes.paragraph}>
          <strong>Platform:</strong>{" "}
          <a href="https://SugarCribs.com" className={classes.link}>SugarCribs.com</a>
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          This Acceptable Use Policy outlines the standards of behavior
          expected from all users and content creators on SugarCribs.com. By
          accessing or using Sugar Cribs, you agree to abide by this policy
          in addition to our Terms of Service and other legal policies.
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          1. Prohibited Content
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          You may not upload, share, or distribute content that includes:
        </Typography>
        <Box component="ul" style={{ paddingLeft: "20px" }}>
          <li className={classes.listItem}>
            <strong>Non-consensual content:</strong> Any media involving
            individuals without their clear, documented consent (including
            hidden cameras or revenge content).
          </li>
          <li className={classes.listItem}>
            <strong>Minors or underage content:</strong> Any individual
            appearing in content must be at least 18 years old, with age
            verification required.
          </li>
          <li className={classes.listItem}>
            <strong>Incest, bestiality, or other illegal acts:</strong>{" "}
            Depictions of or references to illegal sexual behavior or
            criminal activity.
          </li>
          <li className={classes.listItem}>
            <strong>Hate speech or harassment:</strong> Content that
            promotes discrimination, threats, or targeted harassment based
            on race, gender, sexual orientation, religion, or disability.
          </li>
          <li className={classes.listItem}>
            <strong>Extreme violence or abuse:</strong> Graphic violence,
            non-consensual aggression, or torture.
          </li>
          <li className={classes.listItem}>
            <strong>
              Content involving weapons, drugs, or illegal activity.
            </strong>
          </li>
        </Box>

        <Typography variant="h5" className={classes.subtitle}>
          2. Prohibited Behavior
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          You may not:
        </Typography>
        <Box component="ul" style={{ paddingLeft: "20px" }}>
          <li className={classes.listItem}>
            <strong>
              Use the platform to solicit or promote prostitution or escort
              services.
            </strong>
          </li>
          <li className={classes.listItem}>
            <strong>
              Impersonate another individual or misrepresent your identity.
            </strong>
          </li>
          <li className={classes.listItem}>
            <strong>
              Attempt to circumvent platform security or content moderation
              systems.
            </strong>
          </li>
          <li className={classes.listItem}>
            <strong>Exploit, stalk, harass, or dox other users.</strong>
          </li>
          <li className={classes.listItem}>
            <strong>
              Scrape, copy, or redistribute content without permission.
            </strong>
          </li>
          <li className={classes.listItem}>
            <strong>
              Use automation (bots/scripts) to manipulate views, followers,
              or interactions.
            </strong>
          </li>
          <li className={classes.listItem}>
            <strong>
              Post misleading, fraudulent, or deceptive material.
            </strong>
          </li>
          <li className={classes.listItem}>
            <strong>Promote pyramid schemes, spam, or scams.</strong>
          </li>
        </Box>

        <Typography variant="h5" className={classes.subtitle}>
          3. User Safety
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          To protect our community:
        </Typography>
        <Box component="ul" style={{ paddingLeft: "20px" }}>
          <li className={classes.listItem}>
            You may not share personal contact information (email, phone
            numbers, addresses) in public profiles or content.
          </li>
          <li className={classes.listItem}>
            You must report abusive, dangerous, or exploitative behavior
            immediately using our reporting tools or by contacting{" "}
            <a href="mailto:support@sugarcribs.com" className={classes.link}>
              support@sugarcribs.com
            </a>
          </li>
          <li className={classes.listItem}>
            All models and creators must pass our identity verification and
            18+ documentation process (per 18 U.S.C. §2257 compliance).
          </li>
        </Box>

        <Typography variant="h5" className={classes.subtitle}>
          4. Content Ownership and Responsibility
        </Typography>
        <Box component="ul" style={{ paddingLeft: "20px" }}>
          <li className={classes.listItem}>You are solely responsible for the content you upload.</li>
          <li className={classes.listItem}>
            By uploading content, you affirm that you own or have the legal
            rights to distribute it.
          </li>
          <li className={classes.listItem}>
            You agree not to upload content that belongs to another creator
            without written permission.
          </li>
          <li className={classes.listItem}>
            Sugar Cribs reserves the right to remove any content that
            violates this policy without notice.
          </li>
        </Box>

        <Typography variant="h5" className={classes.subtitle}>
          5. Enforcement and Violations
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          Violation of this policy may result in:
        </Typography>
        <Box component="ul" style={{ paddingLeft: "20px" }}>
          <li className={classes.listItem}>Content removal</li>
          <li className={classes.listItem}>Temporary or permanent account suspension</li>
          <li className={classes.listItem}>Reporting to law enforcement if applicable</li>
          <li className={classes.listItem}>Forfeiture of earnings or refunds</li>
        </Box>
        <Typography variant="body1" className={classes.paragraph}>
          Sugar Cribs reserves the right to enforce this policy at our sole
          discretion and may take action without prior warning.
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          6. Changes to this Policy
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          We may update this Acceptable Use Policy from time to time. Users
          will be notified of material changes, and continued use of the
          platform after updates constitutes acceptance of the new terms.
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          7. Contact Us
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          For questions or to report a violation, contact:
        </Typography>

        <Box className={classes.contactInfo}>
          <Typography
            variant="h6"
            style={{ color: "#FE5A97", marginBottom: "16px" }}
          >
            Contact Information
          </Typography>
          <Typography variant="body1" className={classes.paragraph}>
            <strong>Email:</strong>{" "}
            <a href="mailto:support@sugarcribs.com" className={classes.link}>support@sugarcribs.com</a>
          </Typography>
          <Typography variant="body1" className={classes.paragraph}>
            <strong>Website:</strong>{" "}
            <a href="https://SugarCribs.com" className={classes.link}>https://SugarCribs.com</a>
          </Typography>
        </Box>
      </Container>
    </>
  );
}
